/**
 * date-only.ts — parsing/formatting/comparison helpers for DATE-ONLY values
 * (`YYYY-MM-DD`, no time component: `best_before_date`, `due_date`,
 * `invoice_date`, `expiration_date`, `expected_ship_date`, `expiry_date`…).
 *
 * The bug this file exists to kill (owner-reported, verified on prod):
 * `new Date("2024-06-21")` parses the string as **UTC midnight**. Rendering
 * it with `toLocaleDateString()` — or comparing it against `new Date()` (an
 * instant in the browser's LOCAL timezone) — in any negative-offset
 * timezone (Americas) shifts the visible/compared date back a full day:
 * "2024-06-21" reads as "2024-06-20", and a batch that expires "today" reads
 * as already expired yesterday evening.
 *
 * The fix: a date-only string never goes through `new Date(string)`. It is
 * split into (year, month, day) and built via `new Date(y, m - 1, d)` —
 * the LOCAL-midnight constructor overload — so the calendar date the string
 * names is the calendar date that renders and compares, in every timezone.
 *
 * Full ISO datetimes (`created_at`, `updated_at`, `archived_at`,
 * `source_created_at`…) are real instants, not calendar dates — they are
 * NOT date-only and must keep going through the normal `new Date(string)`
 * parse. Every function below falls through to that normal parse for any
 * input that isn't a strict `YYYY-MM-DD` string, so passing a full
 * timestamp through these helpers is a safe no-op (unchanged behavior).
 */

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True when `value` is a strict `YYYY-MM-DD` date-only string — no time
 * component, no timezone offset. (A full ISO datetime like
 * "2024-06-21T00:00:00Z" does NOT match — it keeps the normal parse path.)
 */
export function isDateOnlyString(value: unknown): value is string {
  return typeof value === "string" && DATE_ONLY_PATTERN.test(value);
}

/**
 * Parse any date-ish value into a `Date`, treating a strict `YYYY-MM-DD`
 * string as LOCAL midnight (never UTC midnight). `Date` instances pass
 * through unchanged (invalid ones become `null`). Anything else (full ISO
 * datetimes, numbers, other strings) falls through to the ordinary
 * `new Date(String(value))` parse — identical to pre-fix behavior.
 *
 * Returns `null` for `null`/`undefined`/unparseable input.
 */
export function parseDateOnlyLocal(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (isDateOnlyString(value)) {
    const parts = value.split("-").map(Number);
    const year = parts[0] ?? 0;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Render a date-only or datetime value for display. A strict `YYYY-MM-DD`
 * string parses to local date parts first — so it always formats as the
 * calendar day the string names. Anything else (full ISO timestamps, `Date`
 * instances) formats exactly as `toLocaleDateString()` always has.
 *
 * Returns `""` for `null`/`undefined`/empty, or the raw stringified input
 * if it's unparseable (mirrors the previous formatCell behavior).
 */
export function formatDateOnly(
  value: unknown,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
): string {
  if (value == null || value === "") return "";
  const d = parseDateOnlyLocal(value);
  if (!d) return String(value);
  return d.toLocaleDateString(locales, options);
}

/** Local midnight for a given reference Date (default: now). */
function localMidnight(reference: Date): Date {
  return new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
}

/**
 * Day-granularity "is this date-only value before `reference`'s calendar
 * day (default: today)?" — compares LOCAL calendar date parts, never the
 * epoch milliseconds of a UTC-midnight parse against a local "now". This is
 * the comparison every "is overdue / is expired / is late" site needs
 * instead of `new Date(dateOnlyString) < new Date()`.
 *
 * Returns `false` for `null`/`undefined`/unparseable input (never overdue).
 */
export function isDateOnlyBefore(
  value: unknown,
  reference: Date = new Date(),
): boolean {
  const d = parseDateOnlyLocal(value);
  if (!d) return false;
  return localMidnight(d).getTime() < localMidnight(reference).getTime();
}

/**
 * Whole calendar days between a date-only value and `reference` (default:
 * today) — positive when the value is in the future, negative when past.
 * Computed via `Date.UTC` on the LOCAL date parts of both sides so a DST
 * transition between the two dates never perturbs the count by an hour
 * (which a plain `(d.getTime() - today.getTime()) / 86400000` can).
 *
 * Returns `null` for `null`/`undefined`/unparseable input.
 */
export function daysUntilDateOnly(
  value: unknown,
  reference: Date = new Date(),
): number | null {
  const d = parseDateOnlyLocal(value);
  if (!d) return null;
  const target = localMidnight(d);
  const ref = localMidnight(reference);
  const targetUtc = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const refUtc = Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate());
  return Math.round((targetUtc - refUtc) / 86_400_000);
}
